import { useCallback, useState } from "react";
import { useCartToasts } from "./useCartToasts";
import { CartLabels } from "../utils/cartLabels";

type CartLoadingAction = "reserve" | "remove" | null;

interface UseCartProductActionsParams {
    cartLabels: CartLabels;
    reserveProduct: (productId: number) => { unwrap: () => Promise<unknown> };
    removeReservedProduct: (productId: number) => {
        unwrap: () => Promise<unknown>;
    };
    isReserving: boolean;
    isRemoving: boolean;
}

interface UseCartProductActionsReturn {
    isReserving: boolean;
    isRemoving: boolean;
    isReservingProduct: (productId: number) => boolean;
    isRemovingProduct: (productId: number) => boolean;
    removingItemId: string | null;
    reserveProductById: (productId: number) => Promise<void>;
    removeProductById: (productId: number) => Promise<void>;
}

export const useCartProductActions = ({
    cartLabels,
    reserveProduct,
    removeReservedProduct,
    isReserving,
    isRemoving,
}: UseCartProductActionsParams): UseCartProductActionsReturn => {
    const { showSuccessToast, showRemovedToast, showErrorToast } =
        useCartToasts(cartLabels);

    const [loadingProductId, setLoadingProductId] = useState<number | null>(
        null,
    );
    const [loadingAction, setLoadingAction] = useState<CartLoadingAction>(null);

    const isReservingProduct = useCallback(
        (productId: number): boolean =>
            loadingProductId === productId && loadingAction === "reserve",
        [loadingProductId, loadingAction],
    );

    const isRemovingProduct = useCallback(
        (productId: number): boolean =>
            loadingProductId === productId && loadingAction === "remove",
        [loadingProductId, loadingAction],
    );

    const removingItemId =
        loadingAction === "remove" && loadingProductId !== null
            ? String(loadingProductId)
            : null;

    const reserveProductById = useCallback(
        async (productId: number) => {
            if (loadingProductId !== null) {
                return;
            }

            setLoadingProductId(productId);
            setLoadingAction("reserve");

            try {
                await reserveProduct(productId).unwrap();
                showSuccessToast();
            } catch {
                showErrorToast();
            } finally {
                setLoadingProductId(null);
                setLoadingAction(null);
            }
        },
        [loadingProductId, reserveProduct, showSuccessToast, showErrorToast],
    );

    const removeProductById = useCallback(
        async (productId: number) => {
            if (loadingProductId !== null) {
                return;
            }

            setLoadingProductId(productId);
            setLoadingAction("remove");

            try {
                await removeReservedProduct(productId).unwrap();
                showRemovedToast(productId);
            } catch {
                showErrorToast();
            } finally {
                setLoadingProductId(null);
                setLoadingAction(null);
            }
        },
        [
            loadingProductId,
            removeReservedProduct,
            showRemovedToast,
            showErrorToast,
        ],
    );

    return {
        isReserving,
        isRemoving,
        isReservingProduct,
        isRemovingProduct,
        removingItemId,
        reserveProductById,
        removeProductById,
    };
};
